import { openDB, type DBSchema, type IDBPDatabase } from "idb";

const DB_NAME = "fluit-storm";
const DB_VERSION = 2;

export const MAX_PROJECT_NAME_LENGTH = 100;

export interface StoredProject {
  id: string;
  name: string;
  fileName: string;
  lastModified: number;
  rawSections: ReadonlyMap<string, string[][]>;
  crs: string | null;
  /** The plane view's own background image — its own calibration lives in `rawSections`' `[BACKDROP]` section instead; this is the image data only. */
  backgroundImageBlob?: Blob | null;
  /** A bundled sample opened straight from Landing.tsx. Autosaves like any other project, but `listProjects()` hides it and `purgeEphemeralProjects()` drops it on the next app start — "Save copy as" is the only way to keep it, and always clears this flag on the copy. */
  ephemeral?: boolean;
  /** Content of every external climate file supplied for this project, keyed by the exact `.inp` FILE-column token it answers — persisted (not in-memory-only) so a file supplied once survives a reload. */
  externalFiles?: ReadonlyMap<string, string>;
  /** `parseInpComments`'s own map, captured once when the `.inp` was first opened — every row's own trailing `;comment`, re-attached on export by `serializeInp`'s 2nd argument and shown in `PropertyPanel` for the selected element. Absent for a project created fresh (no file opened yet) or saved before this field existed. */
  comments?: ReadonlyMap<string, string>;
}

export type ProjectSummary = Pick<StoredProject, "id" | "name" | "fileName" | "lastModified">;

/** `ephemeral` isn't part of `ProjectSummary` (never shown to a caller) but IS needed here to answer "which ids are ephemeral" without touching the full `projects` store — see `purgeEphemeralProjects` below. */
interface StoredSummary extends ProjectSummary {
  ephemeral?: boolean;
}

interface SwmmDBSchema extends DBSchema {
  projects: { key: string; value: StoredProject };
  /**
   * A lightweight mirror of `projects`, kept in sync by `saveProject`/
   * `deleteProject` — `listProjects()` used to `getAll()` the FULL `projects`
   * store (every `rawSections`/`backgroundImageBlob`/`externalFiles`, full
   * network data for every saved project) just to build this list, and did
   * so after EVERY autosave (`useAutosave.ts`, every 800ms debounce), scaling
   * with total saved-project count rather than the edited project's own
   * size (P1 deep-review). This store only ever holds the same handful of
   * small fields `ProjectSummary` already exposes.
   */
  summaries: { key: string; value: StoredSummary };
}

function summaryOf(p: StoredProject): StoredSummary {
  return {
    id: p.id,
    name: p.name,
    fileName: p.fileName,
    lastModified: p.lastModified,
    ephemeral: p.ephemeral,
  };
}

/** `crypto.randomUUID()` requires a secure context (HTTPS/localhost) and throws on plain `http://` — falls back to building a v4 UUID from `getRandomValues()`, which works in every context. */
export function generateProjectId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

let dbPromise: Promise<IDBPDatabase<SwmmDBSchema>> | null = null;

function getDb(): Promise<IDBPDatabase<SwmmDBSchema>> {
  // A rejected `openDB` call (e.g. private-browsing mode, quota exceeded)
  // must NOT stay cached here — `dbPromise` is still a truthy Promise
  // object even once it settles rejected, so a plain `??=` would keep
  // returning that same permanently-rejected promise to every future
  // caller for the rest of the session, disabling persistence entirely
  // after one transient failure. Clearing it back to `null` on rejection
  // lets the next call retry a fresh `openDB`.
  dbPromise ??= openDB<SwmmDBSchema>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, _newVersion, transaction) {
      if (oldVersion < 1) db.createObjectStore("projects", { keyPath: "id" });
      if (oldVersion < 2) {
        const summaries = db.createObjectStore("summaries", { keyPath: "id" });
        // Backfill from whatever was already saved under the old (pre-
        // `summaries`) schema — a plain `.then()`, not `await`, since idb's
        // own `upgrade` callback returns `void`: this still runs inside the
        // same versionchange transaction as long as every request stays
        // chained through it (idb's own documented migration pattern).
        transaction
          .objectStore("projects")
          .getAll()
          .then((all) => {
            for (const p of all) summaries.put(summaryOf(p));
          });
      }
    },
  }).catch((err) => {
    dbPromise = null;
    throw err;
  });
  return dbPromise;
}

/** Test-only: drops the cached connection so the next call re-opens against the current `indexedDB` — without this, the first test's connection stays cached for the rest of the file, since tests swap in a fresh `IDBFactory` per case. */
export function _resetDbForTests(): void {
  dbPromise = null;
}

export async function saveProject(project: StoredProject): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(["projects", "summaries"], "readwrite");
  await Promise.all([
    tx.objectStore("projects").put(project),
    tx.objectStore("summaries").put(summaryOf(project)),
    tx.done,
  ]);
}

export async function listProjects(): Promise<ProjectSummary[]> {
  const db = await getDb();
  const all = await db.getAll("summaries");
  return all
    .filter((p) => !p.ephemeral)
    .map(({ id, name, fileName, lastModified }) => ({ id, name, fileName, lastModified }))
    .sort((a, b) => b.lastModified - a.lastModified);
}

export async function purgeEphemeralProjects(): Promise<void> {
  const db = await getDb();
  const all = await db.getAll("summaries");
  const ephemeralIds = all.filter((p) => p.ephemeral).map((p) => p.id);
  const tx = db.transaction(["projects", "summaries"], "readwrite");
  await Promise.all([
    ...ephemeralIds.map((id) => tx.objectStore("projects").delete(id)),
    ...ephemeralIds.map((id) => tx.objectStore("summaries").delete(id)),
    tx.done,
  ]);
}

export async function loadProject(id: string): Promise<StoredProject | undefined> {
  const db = await getDb();
  const project = await db.get("projects", id);
  // Records saved before georeferencing/background images existed have no
  // `crs`/`backgroundImageBlob` field — must normalize to `null`, not leave
  // `undefined`, since callers check `=== null` for "not set".
  return (
    project && {
      ...project,
      crs: project.crs ?? null,
      backgroundImageBlob: project.backgroundImageBlob ?? null,
    }
  );
}

export async function deleteProject(id: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(["projects", "summaries"], "readwrite");
  await Promise.all([
    tx.objectStore("projects").delete(id),
    tx.objectStore("summaries").delete(id),
    tx.done,
  ]);
}
